<template>

  <div class="post suggested-inline" :style="{ marginBottom: `${gap}px` }">
    <!-- Two-column layout with bio underneath: User Info | Follow Button -->

    <div class="suggested-for-you-layout flex items-start  gap-8">
      <!-- Left: Avatar + User Info + Bio -->
      <div class="left-section suggested-grid flex-1 min-w-0">
        <!-- Avatar -->
        <div class="avatar-cell flex-shrink-0">
          <BaseAvatar v-if="!isOrg" :storage-id="getData.storageId" :verified="getData.verified" :href="userRoute"
            :isAdmin="getData.isAdmin && isAdmin" :rating="getData.rating"
            :isCurrentUser="userdata?.id === getData.userId" />
          <BaseAvatar v-else :storage-id="getData.storageId" :verified="getData.verified"
            :isAdmin="getData.isAdmin && isAdmin" :rating="getData.rating"
            :isCurrentUser="userdata?.id === getData.userId" />
        </div>

        <!-- Tier badge directly under avatar, horizontally centered to avatar -->
        <!-- <div v-if="getData.rating" class="badge-cell">
          <div class="badge-aligner">
            <ReputationBadge :score="getData.rating" :showTierName="true"
              :isCurrentUser="userdata?.id === getData.userId" :tierSet="tierSet" size="micro"
              class="tier-badge-under-avatar" />
          </div>
        </div> -->

        <!-- Follow/Unfollow under badge (shown only on tight widths) -->
        <div class="button-cell">
          <BaseButton v-if="isOrg" class="base-btn--small !max-w-[78px] !min-w-[78px]" mode="secondary"
            @click="addUserToOrg">
            Add
          </BaseButton>
          <ButtonFollow v-if="isAuth && data.id !== userdata.id" :loading="followLoading" :isFollow="isFollowing"
            @click="handleFollow(getData.userId)" class="suggested-post__follow-button"
            :class="{ 'suggested-post__unfollow-button': isFollowing }" />
        </div>

        <!-- Name and username line (to the right of avatar) -->
        <div class="name-cell author-name-line flex items-center gap-2 flex-wrap">
          <div v-if="!isOrg"
            class="author-name hover:underline overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
            @click.stop="handleClickBoth">
            {{ fullName }}
          </div>
          <div v-else class="author-name hover:underline overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
            @click.stop="handleClickBoth">
            {{ fullName }}
          </div>

          <!-- Username on same line -->
          <div v-if="getData.username && !isOrg"
            class="author-username text-[0.8rem] text-[var(--text-02)] hover:underline overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
            @click.stop="handleClickBoth">
            @{{ getData.username }}
          </div>
          <div v-else-if="getData.username && isOrg"
            class="author-username text-[0.8rem] text-[var(--text-02)] hover:underline overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
            @click.stop="handleClickBoth">
            @{{ getData.username }}
          </div>
        </div>

        <!-- Bio directly to the right of the badge (second row) -->
        <!-- <div class="bio-cell bio-section mt-0">
          <span v-if="getData.bio" class="bio-text text-text02 cutLines text-xs text-left"
            v-html="formatBio(getData.bio)"></span>
          <span v-else class="bio-text text-text02 text-xs italic opacity-60">
            No bio available
          </span>
        </div> -->
      </div>

      <!-- Right: Follow button -->
      <div class="right-section flex-shrink-0" style="width: 62px;">
        <BaseButton v-if="isOrg" class="base-btn--small !max-w-[78px] !min-w-[78px]" mode="secondary"
          @click="addUserToOrg">
          Add
        </BaseButton>
        <ButtonFollow v-if="isAuth && data.id !== userdata.id" :loading="followLoading" :isFollow="isFollowing"
          @click="handleFollow(getData.userId)" class="suggested-post__follow-button"
          :class="{ 'suggested-post__unfollow-button': isFollowing }" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { useToast } from "primevue/usetoast";
import { useStore } from "vuex";

import { authErrorHandler } from "@/services/error/authHandler.js";
import { useFollowNotification } from "@/utils/notifications/notifiationMessages";
import ReputationBadge from "@/components/ReputationBadge.vue";
import { useTierSet } from "@/utils/reputationTiers.js";

import ButtonFollow from "../../components/ui-kit/ButtonFollow/index.vue";
import getFullName from "../../utils/user/getFullName";
import { makeLegacyDecoration } from "@/utils/urlDecoration.js";

const router = useRouter();

const notify = useFollowNotification();
const toast = useToast();
const store = useStore();
const userdata = computed(() => store.getters.getUser);
const isAdmin = computed(
  () => userdata.value?.roles?.includes("admin") || false
);

// Tier set for reputation badges
const { tierSet } = useTierSet();

const emit = defineEmits(["adduser"]);

const props = defineProps({
  data: {
    type: Object,
    default: null
  },
  type: {
    type: String,
    default: "user"
  },
  isAuth: {
    type: Boolean,
    default: false,
  },
  isOrg: {
    type: Boolean,
    default: false,
  },
  gap: {
    type: [String, Number],
    default: 15,
  },
  onCloseDropdown: {
    type: Function,
    default: null
  }
});

/**
 * Normalize incoming data so templates can safely access fields.
 * Provide a minimal typed-like shape with known keys.
 */
const getData = computed(() => {
  // Normalize incoming data so templates can safely access fields
  let obj = {
    storageId: undefined,
    verified: undefined,
    userId: undefined,
    roles: undefined,
    isAdmin: undefined,
    username: undefined,
    rating: undefined,
    firstName: undefined,
    lastName: undefined,
    bio: undefined,
  };

  if (props.type === "post") {
    const post = props.data || {};
    obj = { ...obj, ...(post || {}) };
    obj.storageId = post.avatarFile?.storageId;
    obj.verified = post.verified;
    obj.userId = post.id;
    obj.roles = post.authorUser?.roles || [];
    obj.isAdmin = Array.isArray(obj.roles) ? obj.roles.includes("admin") : false;
  } else if (props.type === "user") {
    const user = props.data || {};
    obj = { ...obj, ...(user || {}) };
    obj.storageId = user?.avatarFile?.storageId;
    obj.userId = user.id;
    obj.isAdmin = user.roles?.includes("admin") || false;
    obj.username = user.username; // Ensure username is copied

  }

  return obj;
});

// Refresh the current user's profile from the server
const refreshProfile = async () => {
  try {
    await store.dispatch('getMe');
  } catch (e) {
    console.error('Failed to refresh profile after follow/unfollow', e);
  }
};

const handleUserClick = () => {
  const route = (!props.data?.username
    ? `/profile/${props.data?.id}`
    : `/user/${props.data?.username}`) + makeLegacyDecoration();

  router.push(route);
};
const handleClickBoth = () => {
  handleUserClick();
  props.onCloseDropdown?.();
};
const followLoading = ref(false);

const userRoute = computed(() => {
  const route = (!props.data?.username
    ? `/profile/${props.data?.id}`
    : `/user/${props.data?.username}`) + makeLegacyDecoration();

  return route;
});

const isFollowing = computed(() => {
  if (!Array.isArray(userdata.value?.following)) return null;
  return userdata.value.following.includes(getData.value.userId);
});
// const fullName = computed(() => {
//   let firstName = getData.value.firstName;
//   let lastName = getData.value.lastName;
//   let maxWidth = 9;

//   if (!firstName && !lastName) {
//     return getData.value.username;
//   }

//   if (firstName) {
//     if (firstName.length > maxWidth) {
//       firstName = firstName.slice(0, maxWidth) + "..";
//     }
//   } else {
//     firstName = "";
//   }

//   if (lastName) {
//     if (lastName.length > maxWidth) {
//       lastName = lastName.slice(0, maxWidth) + "..";
//     }
//   } else {
//     lastName = "";
//   }

//   if (firstName && lastName) {
//     return `${firstName} ${lastName}`;
//   } else {
//     return firstName || lastName;
//   }
// });

const fullName = computed(() => {
  let firstName = getData.value.firstName;
  let lastName = getData.value.lastName;

  if (!firstName && !lastName) {
    return getData.value.username;
  }

  // Return full name without truncation - CSS will handle overflow
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else {
    return firstName || lastName || "";
  }
});

const addUserToOrg = () => {
  emit("adduser");
};

const handleFollow = async (id) => {
  followLoading.value = true;
  if (!isFollowing.value) {
    try {
      const res = await fetch(`/api/users/${id}/follow`, {
        method: "POST",
      });

      const data = await res.json();
      if (data.statusCode === 403 && data.message === "User blocked you") {
        toast.add({
          severity: "warn",
          summary: "Why can't you do this?",
          detail:
            "This user has blocked you, so you can't perform this action.",
          life: 7000,
        });
        return;
      }
      if (
        (res.status === 401 || res.status === 403) &&
        String(data.message) !== "User blocked you"
      ) {
        authErrorHandler(res);
        return;
      }
      if (data.statusCode === 409) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: data.message,
          life: 7000,
        });
        return;
      }

      if (data.error) {
        const followerName = getFullName(props.data);
        notify("error", "followed", followerName);
      } else {
        await refreshProfile();
        const followerName = getFullName(props.data);
        notify("success", "followed", followerName);
        userdata.value.following.push(id);
      }
    } catch (error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 7000,
      });
    } finally {
      followLoading.value = false;
    }
  } else {
    try {
      const res = await fetch(`/api/users/${id}/unfollow`, {
        method: "POST",
      });
      if (res.status === 401 || res.status === 403) {
        authErrorHandler(res);
        return;
      }
      const data = await res.json();
      if (data.statusCode === 409) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: data.message,
          life: 7000,
        });
        return;
      }
      if (data.error) {
        // Error already handled above
      } else {
        await refreshProfile();
        const followerName = getFullName(props.data);
        notify("success", "unfollowed", followerName);
        userdata.value.following = userdata.value.following.filter(
          (userId) => userId !== id
        );
      }
    } catch (error) {
      store.commit("setUserDataError", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 7000,
      });
    } finally {
      followLoading.value = false;
    }
  }
};

const formatBio = (bio) => {
  return bio.replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank">$1</a>'
  );
};
</script>

<style lang="scss" scoped>
.post.suggested-inline {
  width: 100%;
  display: flex;
  align-items: flex-start;
  background: transparent;
  box-shadow: none;
  border: 0;

  &__avatar-container {
    width: 48px;
    flex-shrink: 0;
  }

  &__user-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__username {
    align-self: flex-start;
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
    font-weight: 400 !important;

    &--secondary {
      color: var(--text-02);
      font-size: 0.75rem;
    }
  }

  &__body {
    flex-grow: 1;
    overflow-wrap: break-word;
    overflow: hidden;
  }

  &__badge {
    width: 48px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1px;
    margin-bottom: 4px;
  }

  &__content {
    margin-top: 4px;
  }
}

// Two-column layout for suggested for you section
.suggested-for-you-layout {
  width: 100%;
  align-items: flex-start;
  gap: 28px;
  /* Tweakable offset to pull the text block closer to the avatar */
  --name-left-pull: 0px;
  /* no left pull by default to avoid overlap */
  /* Allow wrapping at constrained widths to prevent overlap with follow button */
  flex-wrap: nowrap;
  // margin-bottom: 1rem;
}

.left-section {
  flex: 1 1 auto;
  min-width: 0;
  /* Reserve space for the right-side button and gaps to avoid overlap */
  max-width: calc(100% - 110px);
}

/* Grid layout: 2 columns (avatar/badge | text) and 2 rows (name | bio) */
.suggested-grid {
  display: flex;
  gap: 4px;
  // display: grid;
  // grid-template-columns: 88px 1fr;
  // /* allow space for widest badge label like 'Diamond' */
  // grid-template-rows: auto auto auto;
  // /* name | bio | under-badge button */
  // grid-column-gap: 8px;
  // grid-row-gap: 1px;
  // /* slightly tighter vertical spacing */
  // align-items: start;
}

.avatar-cell {
  grid-column: 1;
  grid-row: 1;
}

.badge-cell {
  grid-column: 1;
  grid-row: 2;
  margin-top: 2px;
  /* Ensure space for the widest tier label like 'Diamond' */
  min-width: 88px;
  /* match grid column width */
}

/* Follow/Unfollow button that can move under the badge on tight widths */
.button-cell {
  grid-column: 1;
  grid-row: 3;
  /* sits below the badge */
  display: none;
  /* hidden by default; shown at narrow widths */
  margin-top: 4px;
}

/* Center badge directly under the avatar */
.badge-aligner {
  /* avatar is 48px wide in this context; center badge within the avatar column */
  width: 48px;
  display: flex;
  justify-content: center;
}

.badge-aligner:hover .tier-badge-under-avatar,
.badge-aligner:focus-within .tier-badge-under-avatar {
  filter: drop-shadow(0 0 0.6px var(--surface-border, rgba(0, 0, 0, 0.6))) drop-shadow(0 0 4px rgba(59, 130, 246, 0.2));
}

.name-cell {
  grid-column: 2;
  grid-row: 1;
  /* Visually pull left toward avatar */
  margin-left: 0;
}

.bio-cell {
  grid-column: 2;
  grid-row: 2;
  /* Visually pull left toward avatar */
  margin-left: 0;
}

.bio-section {
  width: 100%;

  .bio-text {
    display: -webkit-box;
    -webkit-line-clamp: 1; // show just one line in compact mode
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.3;
    word-wrap: break-word;
    max-width: 100%;
    hyphens: auto;
  }
}

.right-section {
  flex-shrink: 0;
  width: 78px; // Fixed width for follow button
  display: flex;
  align-items: flex-start;
}

.suggested-post__follow-button {
  margin-bottom: 0 !important;
  transition: 0.2s ease-in;
  font-size: 0.8rem;
  background-color: var(--joine-button);
  /* Keep button within right-section width; prevent overflow */
  width: 70%;
  max-width: 78px;
  min-width: 60px;
  white-space: nowrap;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: white;

  &:hover {
    background-color: var(--post-dropdown);
    color: white;
  }
}

.suggested-post__unfollow-button {
  border: 1px solid var(--interactive-01, #a3e635);
  background-color: transparent;
  color: var(--interactive-01, #a3e635);

  &:hover {
    background-color: var(--interactive-01, #a3e635);
    color: var(--button-on-primary, #000);
  }
}

/* Dark mode: ensure readable text on lime background */
[scheme='dark'] .suggested-post__follow-button,
[scheme='dark'] .suggested-post__unfollow-button:hover {
  color: white !important;
}

/* Slightly smaller badge so it doesn't encroach into bio */
.tier-badge-under-avatar {
  transform: scale(1.1);
  /* ~10% larger for visibility */
  transform-origin: left top;
  display: inline-block;
  /* Thin outline for contrast over varied backgrounds */
  filter: drop-shadow(0 0 0.6px var(--surface-border, rgba(0, 0, 0, 0.45)));
  transition: filter .15s ease, transform .15s ease;
}

// Author info alignment
.author-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  font-size: 12px;
  line-height: 1.2;
  min-width: 0;
  width: 100%;
}

.author-name-line {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0px;
  flex-wrap: wrap;
  min-width: 0;
}

.author-name {
  font-weight: 600;
  color: var(--text-primary);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
}

.author-username {
  color: var(--text-02);
  font-size: 0.8rem;
  flex-shrink: 0;
}

.tier-badge-container {
  margin-top: 0;
}

/* Compact avatar size for suggested block */
.avatar-compact :deep(img),
.avatar-compact :deep(.avatar),
.avatar-compact :deep(.base-avatar),
.avatar-compact :deep(svg) {
  width: 36px !important;
  height: 36px !important;
}

/* Inline badge spacing and scale */
.tier-badge-inline {
  display: inline-flex;
  align-items: center;
  transform: scale(1.32);
  /* ~10% larger inline badge */
  transform-origin: left center;
  margin-left: 2px;
  margin-top: 1px;
  filter: drop-shadow(0 0 0.6px var(--surface-border, rgba(0, 0, 0, 0.45)));
  transition: filter .15s ease;
}

.tier-badge-inline:hover,
.tier-badge-inline:focus {
  filter: drop-shadow(0 0 0.6px var(--surface-border, rgba(0, 0, 0, 0.6))) drop-shadow(0 0 4px rgba(59, 130, 246, 0.2));
}

/* Responsive safeguards to avoid overlap on narrow rightbar widths */
/* Container query: respond to the actual rightbar suggested block width */
@container suggested-block (max-width: 320px) {

  /* When the suggested block (container) is narrow, move the button under the badge */
  .suggested-for-you-layout {
    flex-wrap: wrap;
  }

  .left-section {
    width: 100%;
    max-width: 100%;
  }

  .button-cell {
    display: block;
  }

  .right-section {
    display: none;
  }
}

@media (max-width: 980px) {

  /* Tighten avatar grid so text has a bit more room */
  .suggested-grid {
    grid-template-columns: 72px 1fr;
  }

  .badge-cell {
    min-width: 72px;
  }
}

@container suggested-block (max-width: 280px) {

  /* Maintain under-badge button in extremely tight rightbar widths */
  .button-cell {
    display: block;
  }

  .right-section {
    display: none;
  }

  .suggested-post__follow-button {
    min-width: 78px;
    width: auto;
  }
}

/* Fallback for browsers without container queries */
@supports not (container-type: inline-size) {
  @media (max-width: 1120px) {
    .suggested-for-you-layout {
      flex-wrap: wrap;
    }

    .left-section {
      width: 100%;
      max-width: 100%;
    }

    .button-cell {
      display: block;
    }

    .right-section {
      display: none;
    }
  }
}
</style>
